import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import api from '../services/api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const title = 'Contact TradeRadar | Get in Touch';
  const description =
    'Contact TradeRadar with questions, feedback, or partnership ideas related to global trade risk intelligence, chokepoints, and policy analysis.';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/contact', form);
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'We could not deliver your message. Please email contact@traderadarglobal.com directly.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path="/contact"
        keywords="contact TradeRadar, trade risk questions, chokepoint analysis contact"
      />

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">
          Contact TradeRadar
        </h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          Questions, corrections, and partnership ideas: use the form below or email{' '}
          <a href="mailto:contact@traderadarglobal.com" className="font-mono text-primary-600 dark:text-primary hover:underline">
            contact@traderadarglobal.com
          </a>
          . We aim to respond to editorial and data issues within several business days.
        </p>
      </header>

      <section className="mb-6">
        <form
          onSubmit={handleSubmit}
          className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 space-y-4"
        >
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-1"
            >
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className="w-full rounded-stitch-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-outline-variant/25 dark:bg-surface-container-high dark:text-on-surface"
              required
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-1"
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className="w-full rounded-stitch-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-outline-variant/25 dark:bg-surface-container-high dark:text-on-surface"
              required
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-1"
            >
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              className="w-full rounded-stitch-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-outline-variant/25 dark:bg-surface-container-high dark:text-on-surface"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-intel-primary inline-flex items-center text-sm disabled:opacity-60"
          >
            {loading ? 'Sending…' : 'Send Message'}
          </button>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2" role="alert">
              {error}
            </p>
          )}

          {submitted && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-2">
              Thank you — your message was received. For urgent matters, email contact@traderadarglobal.com
              directly.
            </p>
          )}
        </form>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          Data & Privacy Note
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          The contact form is intended for general enquiries about the platform. Please avoid
          sending sensitive personal information. For more details on how data is handled, see the
          Privacy Policy.
        </p>
      </section>
    </article>
  );
};

export default Contact;

